/* ═══════════════════════════════════════════════════════════════
   DeliverablesPanel — manage deliverables (Sheet). Same editor table
   the takeover uses (add/edit/delete), just in a side panel instead
   of full-bleed — the Gantt timeline is a takeover-only view (EDIT-COST
   LAW: "manage a list" is a panel; the Gantt is part of "the full tool").
   ═══════════════════════════════════════════════════════════════ */
import React, { useEffect, useRef } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../../../components/ui/sheet';
import { Button } from '../../../components/ui/button';
import { DeliverableEditorTable } from '../widgets/deliverables-widget';
import type { ProjectWidgetCtx } from '../widgets/context';

export function DeliverablesPanel({
  ctx,
  open,
  onClose,
}: {
  ctx: ProjectWidgetCtx;
  open: boolean;
  onClose: () => void;
}) {
  // Radix's Dialog only restores focus to a <Dialog.Trigger> ref on close —
  // this kit's Sheets are always opened imperatively from a page-owned
  // button, never a <SheetTrigger>, so that ref is always null and focus
  // silently lands on <body> (DESIGN.md §7). Capture whatever was focused
  // right before open and restore it ourselves via onCloseAutoFocus.
  const openerRef = useRef<Element | null>(null);
  useEffect(() => {
    if (open) openerRef.current = document.activeElement;
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="!w-[clamp(28rem,44vw,52rem)]"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
        }}
      >
        <SheetHeader>
          <SheetTitle>Manage deliverables</SheetTitle>
        </SheetHeader>
        <div style={{ marginTop: 'var(--space-4)', flex: 1, overflowY: 'auto' }}>
          <DeliverableEditorTable ctx={ctx} open={open} />
        </div>
        <SheetFooter>
          <Button variant="outline" onClick={onClose}>
            Done
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
